import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import CancelButton from "components/CancelButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import SubmitHookFormTS from "components/SubmitHookFormTS";
import { FeeTypeType } from "../../types";
import {
  addStateFeeType,
  editStateFeeType,
  feeTypeSaving,
  rowEditIdStateFeeType,
  selectedRowsStateFeeType,
} from "../atoms/atoms";
import useAddFeeType from "../hooks/useAddFeeType";
import useEditFeeType from "../hooks/useUpdateFeeType";
import FeeTypeInput from "./FeeTypeInput";

export const FeeTypeForm = () => {
  // atoms
  const [editData, setEditData] = useRecoilState(rowEditIdStateFeeType);
  const [isSaving, setIsSaving] = useRecoilState(feeTypeSaving);
  const [isAddState, setIsAddState] = useRecoilState(addStateFeeType);
  const [isEditState, setIsEditState] = useRecoilState(editStateFeeType);
  const [, setSelectedRows] = useRecoilState(selectedRowsStateFeeType);

  // hooks
  const addFeeType = useAddFeeType();
  const updateFeeType = useEditFeeType();

  // local
  const [newData, setNewData] = useState<FeeTypeType>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // form methods
  const useFormMethods = useForm({
    defaultValues: { ...(editData as unknown as FeeTypeType) },
  });

  const onSubmit = (data: FeeTypeType) => {
    const value = {
      ...data,
      feeLevelName: data.feeTypeName.trim(),
      isActive: data.isActive ?? false,
    };

    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
    setSelectedRows([]);
  };

  const handleSave = () => {
    if (newData) {
      if (isEditState) {
        updateFeeType(newData);
      } else {
        addFeeType(newData);
      }
    }
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(null);
    setIsAddState(false);
    setIsEditState(false);
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Fee Type" : "Edit Fee Type"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookFormTS
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <FeeTypeInput data={editData!} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Fee Type" : "Update Fee Type"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
